/**
 * DonutChart — share-of-total view for categorical drills (research: donuts
 * read well at ≤8 slices, so top 8 + "Other"). Center shows the total; hover
 * a slice or legend row to highlight; click drills in (same contract as the
 * ranked bars).
 */

import { useMemo, useState } from "react";
import { moneyFmt } from "./cards.js";

export interface DonutSlice {
  key: string;
  value: number;
  /** Optional display label (defaults to key) — lets a caller show e.g. a SKU code
   *  plus its product name in the legend while drilling still keys off `key`. */
  label?: string;
}

const SLICE_COLORS = [
  "var(--accent)",
  "var(--c2)",
  "var(--good)",
  "var(--accent-2)",
  "var(--bad)",
  "#8b5cf6",
  "#0ea5e9",
  "#f59e0b",
];
const OTHER_COLOR = "var(--body-faint)";
const MAX_SLICES = 8;

export function DonutChart({
  slices,
  money,
  onPick,
  onShowAll,
}: {
  slices: DonutSlice[];
  money: boolean;
  /** Click a slice/legend row (not "Other") to drill into it. */
  onPick?: (key: string) => void;
  onShowAll?: () => void;
}): React.ReactElement {
  const [hover, setHover] = useState<string | null>(null);

  const { shown, total } = useMemo(() => {
    const positive = slices.filter((s) => s.value > 0);
    const sum = positive.reduce((t, s) => t + s.value, 0);
    const top = positive.slice(0, MAX_SLICES);
    const rest = positive.slice(MAX_SLICES);
    const restSum = rest.reduce((t, s) => t + s.value, 0);
    const out = restSum > 0 ? [...top, { key: `Other (${rest.length})`, value: restSum, isOther: true }] : top;
    return { shown: out as Array<DonutSlice & { isOther?: boolean }>, total: sum };
  }, [slices]);

  if (total <= 0) {
    return <div style={{ color: "var(--faint)", padding: "18px 0" }}>Nothing to chart for these filters.</div>;
  }

  const fmt = (v: number): string => (money ? moneyFmt(v) : Math.round(v).toLocaleString());
  const R = 86, INNER = 54, CX = 110, CY = 110;
  let angle = -Math.PI / 2; // start at 12 o'clock

  const arcs = shown.map((s, i) => {
    const frac = s.value / total;
    const a0 = angle;
    const a1 = angle + frac * Math.PI * 2;
    angle = a1;
    // Slice path: outer arc a0→a1, inner arc back. Full-circle single slice
    // breaks the arc command — fudge by 0.0001 rad.
    const sweep = Math.min(a1 - a0, Math.PI * 2 - 0.0001);
    const large = sweep > Math.PI ? 1 : 0;
    const x0 = CX + R * Math.cos(a0), y0 = CY + R * Math.sin(a0);
    const x1 = CX + R * Math.cos(a0 + sweep), y1 = CY + R * Math.sin(a0 + sweep);
    const xi0 = CX + INNER * Math.cos(a0 + sweep), yi0 = CY + INNER * Math.sin(a0 + sweep);
    const xi1 = CX + INNER * Math.cos(a0), yi1 = CY + INNER * Math.sin(a0);
    const d = `M${x0.toFixed(2)},${y0.toFixed(2)} A${R},${R} 0 ${large} 1 ${x1.toFixed(2)},${y1.toFixed(2)} L${xi0.toFixed(2)},${yi0.toFixed(2)} A${INNER},${INNER} 0 ${large} 0 ${xi1.toFixed(2)},${yi1.toFixed(2)} Z`;
    const color = s.isOther ? OTHER_COLOR : SLICE_COLORS[i % SLICE_COLORS.length]!;
    return { ...s, d, color, share: frac };
  });

  const hovered = arcs.find((a) => a.key === hover);

  return (
    <div style={{ display: "flex", gap: 26, alignItems: "center", flexWrap: "wrap" }}>
      <svg viewBox="0 0 220 220" width={230} style={{ flexShrink: 0 }} role="img" aria-label="Share of total">
        {arcs.map((a) => (
          <path
            key={a.key}
            d={a.d}
            fill={a.color}
            opacity={hover === null || hover === a.key ? (a.isOther ? 0.55 : 1) : 0.25}
            style={{ cursor: (a.isOther ? onShowAll : onPick) ? "pointer" : "default", transition: "opacity .12s ease" }}
            onMouseEnter={() => setHover(a.key)}
            onMouseLeave={() => setHover(null)}
            onClick={() => a.isOther ? onShowAll?.() : onPick?.(a.key)}
          />
        ))}
        <text x={CX} y={CY - 6} textAnchor="middle" fontSize={13} fontWeight={700} fill="var(--num)" className="dd-num">
          {fmt(hovered?.value ?? total)}
        </text>
        <text x={CX} y={CY + 12} textAnchor="middle" fontSize={10} fill="var(--faint)">
          {hovered ? `${(hovered.share * 100).toFixed(1)}% of total` : "total"}
        </text>
      </svg>
      <div style={{ flex: "1 1 240px", minWidth: 0 }}>
        {arcs.map((a) => (
          <div
            key={a.key}
            onMouseEnter={() => setHover(a.key)}
            onMouseLeave={() => setHover(null)}
            onClick={() => a.isOther ? onShowAll?.() : onPick?.(a.key)}
            role={(a.isOther ? onShowAll : onPick) ? "button" : undefined}
            tabIndex={(a.isOther ? onShowAll : onPick) ? 0 : undefined}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                if (a.isOther) onShowAll?.();
                else onPick?.(a.key);
              }
            }}
            className="dd-drill-row"
            style={{
              display: "flex", alignItems: "center", gap: 9, padding: "5.5px 8px", margin: "0 -8px",
              borderRadius: 7, cursor: (a.isOther ? onShowAll : onPick) ? "pointer" : "default",
              opacity: hover === null || hover === a.key ? 1 : 0.5,
            }}
            title={a.isOther ? onShowAll ? "Show all in a table" : "Other products combined" : onPick ? "Click to drill in" : undefined}
          >
            <span style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, background: a.color }} />
            <span title={a.label ?? a.key} style={{ flex: 1, color: "var(--text)", fontSize: 12.5, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.label ?? a.key}</span>
            <span className="dd-num" style={{ color: "var(--body-faint)", fontSize: 11.5, width: 48, textAlign: "right" }}>{(a.share * 100).toFixed(1)}%</span>
            <span className="dd-num" style={{ color: "var(--num)", fontSize: 12.5, fontWeight: 600, width: 92, textAlign: "right" }}>{fmt(a.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
